import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import AdminPeopleTab from '@/components/admin/AdminPeopleTab';
import AdminReportsTab from '@/components/admin/AdminReportsTab';
import { getAdminDashboard, type AdminDashboardData } from '@/api/adminService';
import { type Period, PERIOD_LABELS, getPeriodDates } from '@/utils/period';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from 'recharts';

const PROVIDER_COLORS: Record<string, string> = {
  chatgpt: '#10a37f',
  claude: '#d97706',
  gemini: '#4285f4',
  perplexity: '#1a73e8',
  grok: '#ef4444',
  deepseek: '#8b5cf6',
};

const MODEL_COLORS = [
  '#4285f4', '#e14944', '#00a944', '#d97706', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316',
];

const SESSION_KEY = 'admin_api_key';

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatCost(value: number): string {
  if (value < 0.01) return `$${value.toFixed(6)}`;
  return `$${value.toFixed(4)}`;
}

const formatChartDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const DailyCostTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const formattedDate = formatChartDate(String(label));

    return (
      <div className="bg-white border border-[rgba(2,2,3,0.1)] rounded p-3 shadow-lg">
        <p className="font-mono text-xs text-[#020203] opacity-60 mb-2">{formattedDate}</p>
        {payload
          .filter((entry: any) => entry.value != null && typeof entry.value === 'number')
          .map((entry: any, index: number) => {
            const name = typeof entry.name === 'string' ? entry.name : String(entry.dataKey ?? index);
            const val = name === 'Calls' ? entry.value.toLocaleString() : formatCost(entry.value);
            return (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="font-mono text-xs uppercase tracking-wide">
                  {name}:
                </span>
                <span className="font-semibold">{val}</span>
              </div>
            );
          })}
      </div>
    );
  }
  return null;
};

export default function AdminDashboardPage() {
  const [apiKey, setApiKey] = useState('');
  const [inputKey, setInputKey] = useState('');
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  type AdminPeriod = Period | 'custom';
  const [period, setPeriod] = useState<AdminPeriod>('last_30_days');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const periodDropdownRef = useRef<HTMLDivElement>(null);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [activeTab, setActiveTab] = useState('analytics');

  const periodOptions: AdminPeriod[] = ['today', 'this_week', 'this_month', 'last_30_days', 'last_90_days', 'all_time', 'custom'];
  const adminPeriodLabels: Record<AdminPeriod, string> = { ...PERIOD_LABELS, custom: 'Custom Range' };

  const { start: periodStart, end: periodEnd } = useMemo(() => {
    if (period === 'custom') {
      if (customStart && customEnd) {
        return { start: new Date(customStart).toISOString(), end: new Date(customEnd + 'T23:59:59').toISOString() };
      }
      return getPeriodDates('last_30_days');
    }
    return getPeriodDates(period);
  }, [period, customStart, customEnd]);

  const fetchDashboard = useCallback(async (key: string, start?: string, end?: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await getAdminDashboard(key, start, end);
      setData(result);
      sessionStorage.setItem(SESSION_KEY, key);
      setApiKey(key);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(SESSION_KEY);
        setApiKey('');
        setError('Invalid API key. Please try again.');
      } else {
        setError('Failed to load dashboard. Please try again.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      setApiKey(stored);
      fetchDashboard(stored, periodStart, periodEnd);
    }
  }, [fetchDashboard, periodStart, periodEnd]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(event.target as Node)) {
        setShowPeriodDropdown(false);
      }
    };
    if (showPeriodDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPeriodDropdown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputKey.trim()) {
      fetchDashboard(inputKey.trim(), periodStart, periodEnd);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setApiKey('');
    setData(null);
    setInputKey('');
  };

  // All hooks must be above the early return to satisfy Rules of Hooks
  const dailyProviderCosts = data?.dailyProviderCosts ?? [];
  const dailyModelCosts = data?.dailyModelCosts ?? [];

  const providerNames = useMemo(() => [...new Set(dailyProviderCosts.map((d) => d.provider))], [dailyProviderCosts]);
  const dailyProviderChartData = useMemo(() => {
    const byDay: Record<string, Record<string, number>> = {};
    for (const row of dailyProviderCosts) {
      if (!byDay[row.day]) byDay[row.day] = {};
      byDay[row.day][row.provider] = row.cost;
    }
    return Object.entries(byDay)
      .map(([day, providers]) => ({ day, ...providers }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [dailyProviderCosts]);

  const modelNames = useMemo(() => [...new Set(dailyModelCosts.map((d) => d.modelId))], [dailyModelCosts]);
  const dailyModelChartData = useMemo(() => {
    const byDay: Record<string, Record<string, number>> = {};
    for (const row of dailyModelCosts) {
      if (!byDay[row.day]) byDay[row.day] = {};
      byDay[row.day][row.modelId] = row.cost;
    }
    return Object.entries(byDay)
      .map(([day, models]) => ({ day, ...models }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [dailyModelCosts]);

  if (!apiKey || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Admin Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter API Key"
                value={inputKey}
                onChange={(e) => setInputKey(e.target.value)}
                autoFocus
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || !inputKey.trim()}>
                {loading ? 'Loading...' : 'Enter'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { subscriptions, costs, summary, topCostlyUsers, modelUsage, queryStats, usersStats } = data;

  const chartData = [...costs.last30Days].reverse();
  const queryChartData = [...queryStats.dailyQueries].reverse();

  return (
    <div className="min-h-screen bg-background p-4 md:p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
        <div className="flex items-center gap-3">
          {activeTab === 'analytics' && (
            <div className="relative" ref={periodDropdownRef}>
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="bg-white border border-[rgba(2,2,3,0.06)] flex gap-3 h-9 items-center justify-center px-3 py-2 rounded hover:opacity-80 transition-opacity"
              >
                <span className="font-sans font-[450] leading-none text-[#020203] text-sm" style={{ fontVariationSettings: "'wdth' 100" }}>
                  {period === 'custom' && customStart && customEnd
                    ? `${customStart} – ${customEnd}`
                    : adminPeriodLabels[period]}
                </span>
                <svg className="h-3 w-3 text-[#353535]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showPeriodDropdown && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-[rgba(2,2,3,0.06)] rounded shadow-lg z-10 min-w-[180px] py-1">
                  {periodOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setPeriod(option); if (option !== 'custom') setShowPeriodDropdown(false); }}
                      className={`w-full text-left px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase transition-colors ${
                        period === option
                          ? 'bg-[rgba(2,2,3,0.04)] text-[#020203]'
                          : 'text-[#020203] hover:bg-[rgba(2,2,3,0.02)]'
                      }`}
                    >
                      {adminPeriodLabels[option]}
                    </button>
                  ))}
                  {period === 'custom' && (
                    <div className="px-4 py-3 border-t border-[rgba(2,2,3,0.06)] space-y-2">
                      <div className="flex flex-col gap-1">
                        <label className="font-mono text-[10px] uppercase tracking-wider text-[#020203] opacity-50">Start</label>
                        <input
                          type="date"
                          value={customStart}
                          onChange={(e) => setCustomStart(e.target.value)}
                          className="border border-[rgba(2,2,3,0.1)] rounded px-2 py-1.5 text-sm w-full"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="font-mono text-[10px] uppercase tracking-wider text-[#020203] opacity-50">End</label>
                        <input
                          type="date"
                          value={customEnd}
                          onChange={(e) => setCustomEnd(e.target.value)}
                          className="border border-[rgba(2,2,3,0.1)] rounded px-2 py-1.5 text-sm w-full"
                        />
                      </div>
                      <button
                        onClick={() => setShowPeriodDropdown(false)}
                        disabled={!customStart || !customEnd}
                        className="w-full mt-1 px-3 py-1.5 bg-[#020203] text-white text-xs font-medium rounded disabled:opacity-30 hover:opacity-80 transition-opacity"
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="people">People & Resources</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="people">
          <AdminPeopleTab apiKey={apiKey} />
        </TabsContent>

        <TabsContent value="reports">
          <AdminReportsTab apiKey={apiKey} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-8">

      {/* Section 1: Revenue & Cost Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Projected Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">
              {formatUsd(summary.projectedMonthlyRevenue)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {subscriptions.total} active subscribers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Monthly LLM Cost (30d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-600">
              {formatUsd(summary.monthlyLlmCost)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Last 30 days total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Profit Margin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${summary.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {summary.profitMargin}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Revenue minus LLM costs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Projected Annual Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">
              {formatUsd(summary.projectedAnnualRevenue)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              MRR x 12
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Section 1b: Usage & Users Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-foreground">
              {usersStats.totalUsers.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              +{usersStats.newThisMonth} this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Users (30d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600">
              {usersStats.activeUsers30d.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {usersStats.totalResources.toLocaleString()} websites tracked
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Queries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-foreground">
              {queryStats.totalQueries.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {queryStats.queriesLast30Days.toLocaleString()} last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Mention Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${queryStats.mentionRate >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
              {queryStats.mentionRate}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {usersStats.totalResources} websites tracked
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Section 2: Active Subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Active Subscriptions ({subscriptions.total})</CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptions.plans.length === 0 ? (
            <p className="text-muted-foreground text-sm">No active subscriptions</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Plan</th>
                    <th className="pb-2 pr-4">Tier</th>
                    <th className="pb-2 pr-4">Interval</th>
                    <th className="pb-2 pr-4 text-right">Count</th>
                    <th className="pb-2 pr-4 text-right">Price</th>
                    <th className="pb-2 text-right">MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.plans.map((plan) => (
                    <tr key={plan.name} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{plan.name}</td>
                      <td className="py-2 pr-4">{plan.tier || '-'}</td>
                      <td className="py-2 pr-4">{plan.interval}</td>
                      <td className="py-2 pr-4 text-right">{plan.count}</td>
                      <td className="py-2 pr-4 text-right">{formatUsd(plan.amountCents / 100)}</td>
                      <td className="py-2 text-right font-medium">{formatUsd(plan.mrr)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold">
                    <td className="pt-2" colSpan={5}>Total MRR</td>
                    <td className="pt-2 text-right">{formatUsd(subscriptions.totalMrr)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Cost by LLM Provider */}
      <Card>
        <CardHeader>
          <CardTitle>Cost by LLM Provider</CardTitle>
        </CardHeader>
        <CardContent>
          {costs.byProvider.length === 0 ? (
            <p className="text-muted-foreground text-sm">No cost data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Provider</th>
                    <th className="pb-2 pr-4 text-right">Total Cost</th>
                    <th className="pb-2 pr-4 text-right">Calls</th>
                    <th className="pb-2 text-right">Avg Cost/Call</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.byProvider.map((p) => (
                    <tr key={p.provider} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium capitalize">{p.provider}</td>
                      <td className="py-2 pr-4 text-right">{formatCost(p.totalCost)}</td>
                      <td className="py-2 pr-4 text-right">{p.calls.toLocaleString()}</td>
                      <td className="py-2 text-right">{formatCost(p.avgCostPerCall)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section: Daily Cost by Provider Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Cost by Provider (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyProviderChartData.length === 0 ? (
            <p className="text-muted-foreground text-sm">No cost data</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={dailyProviderChartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,2,3,0.06)" />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatChartDate}
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                  tickFormatter={(v: number) => `$${v}`}
                />
                <Tooltip content={<DailyCostTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: 16 }}
                  iconType="circle"
                  iconSize={8}
                  formatter={(value: string) => (
                    <span className="font-mono text-xs uppercase tracking-wide text-[#020203] opacity-70">
                      {value}
                    </span>
                  )}
                />
                {providerNames.map((name) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={(entry: Record<string, number>) => entry[name]}
                    name={name}
                    stroke={PROVIDER_COLORS[name] || '#888'}
                    strokeWidth={2}
                    dot={{ fill: PROVIDER_COLORS[name] || '#888', strokeWidth: 0, r: 2 }}
                    activeDot={{ r: 4, fill: PROVIDER_COLORS[name] || '#888' }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Section: LLM Model Usage */}
      <Card>
        <CardHeader>
          <CardTitle>LLM Model Usage</CardTitle>
        </CardHeader>
        <CardContent>
          {modelUsage.length === 0 ? (
            <p className="text-muted-foreground text-sm">No model usage data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Model</th>
                    <th className="pb-2 pr-4">Provider</th>
                    <th className="pb-2 pr-4 text-right">Calls</th>
                    <th className="pb-2 pr-4 text-right">Input Tokens</th>
                    <th className="pb-2 pr-4 text-right">Output Tokens</th>
                    <th className="pb-2 pr-4 text-right">Total Cost</th>
                    <th className="pb-2 text-right">Avg/Call</th>
                  </tr>
                </thead>
                <tbody>
                  {modelUsage.map((m) => (
                    <tr key={m.modelId} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{m.modelId}</td>
                      <td className="py-2 pr-4 capitalize">{m.provider}</td>
                      <td className="py-2 pr-4 text-right">{m.calls.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right">{m.inputTokens.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right">{m.outputTokens.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right">{formatCost(m.totalCost)}</td>
                      <td className="py-2 text-right">{formatCost(m.avgCostPerCall)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section: Daily Cost by Model Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Cost by Model (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyModelChartData.length === 0 ? (
            <p className="text-muted-foreground text-sm">No cost data</p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={dailyModelChartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,2,3,0.06)" />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatChartDate}
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                  tickFormatter={(v: number) => `$${v}`}
                />
                <Tooltip content={<DailyCostTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: 16 }}
                  iconType="circle"
                  iconSize={8}
                  formatter={(value: string) => (
                    <span className="font-mono text-xs uppercase tracking-wide text-[#020203] opacity-70">
                      {value}
                    </span>
                  )}
                />
                {modelNames.map((name, i) => {
                  const color = MODEL_COLORS[i % MODEL_COLORS.length];
                  return (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={(entry: Record<string, number>) => entry[name]}
                      name={name}
                      stroke={color}
                      strokeWidth={2}
                      dot={{ fill: color, strokeWidth: 0, r: 2 }}
                      activeDot={{ r: 4, fill: color }}
                      connectNulls
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Section: Queries by Source */}
      <Card>
        <CardHeader>
          <CardTitle>Queries by Source</CardTitle>
        </CardHeader>
        <CardContent>
          {queryStats.bySource.length === 0 ? (
            <p className="text-muted-foreground text-sm">No query data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Source</th>
                    <th className="pb-2 text-right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {queryStats.bySource.map((s) => (
                    <tr key={s.source} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{s.source}</td>
                      <td className="py-2 text-right">{s.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section: Daily Queries Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Queries (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          {queryChartData.length === 0 ? (
            <p className="text-muted-foreground text-sm">No query data</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={queryChartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,2,3,0.06)" />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatChartDate}
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (active && payload && payload.length && typeof payload[0]?.value === 'number') {
                      return (
                        <div className="bg-white border border-[rgba(2,2,3,0.1)] rounded p-3 shadow-lg">
                          <p className="font-mono text-xs text-[#020203] opacity-60 mb-2">
                            {formatChartDate(String(label))}
                          </p>
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#4285f4' }} />
                            <span className="font-mono text-xs uppercase tracking-wide">Queries:</span>
                            <span className="font-semibold">{payload[0].value.toLocaleString()}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="count" fill="#4285f4" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Cost by Context */}
      <Card>
        <CardHeader>
          <CardTitle>Cost by Context</CardTitle>
        </CardHeader>
        <CardContent>
          {costs.byContext.length === 0 ? (
            <p className="text-muted-foreground text-sm">No cost data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Context</th>
                    <th className="pb-2 pr-4 text-right">Total Cost</th>
                    <th className="pb-2 text-right">Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.byContext.map((c) => (
                    <tr key={c.context} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{c.context}</td>
                      <td className="py-2 pr-4 text-right">{formatCost(c.totalCost)}</td>
                      <td className="py-2 text-right">{c.calls.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 5: Most Costly Users */}
      <Card>
        <CardHeader>
          <CardTitle>Most Costly Users (Top 10)</CardTitle>
        </CardHeader>
        <CardContent>
          {topCostlyUsers.length === 0 ? (
            <p className="text-muted-foreground text-sm">No user cost data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Email</th>
                    <th className="pb-2 pr-4">Profile ID</th>
                    <th className="pb-2 pr-4 text-right">Total Cost</th>
                    <th className="pb-2 pr-4 text-right">Calls</th>
                    <th className="pb-2 text-right">First Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {topCostlyUsers.map((u) => (
                    <tr key={u.profileId} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-sm">
                        {u.email || '-'}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {u.profileId.slice(0, 8)}...
                      </td>
                      <td className="py-2 pr-4 text-right">{formatCost(u.totalCost)}</td>
                      <td className="py-2 pr-4 text-right">{u.calls.toLocaleString()}</td>
                      <td className="py-2 text-right">
                        {new Date(u.firstSeen).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 6: Daily Cost Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Cost Trend (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-muted-foreground text-sm">No cost data</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,2,3,0.06)" />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatChartDate}
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                />
                <YAxis
                  yAxisId="cost"
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                  tickFormatter={(v: number) => `$${v}`}
                />
                <YAxis
                  yAxisId="calls"
                  orientation="right"
                  tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
                  allowDecimals={false}
                />
                <Tooltip content={<DailyCostTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: 16 }}
                  iconType="circle"
                  iconSize={8}
                  formatter={(value: string) => (
                    <span className="font-mono text-xs uppercase tracking-wide text-[#020203] opacity-70">
                      {value}
                    </span>
                  )}
                />
                <Line
                  yAxisId="cost"
                  type="monotone"
                  dataKey="cost"
                  name="Cost"
                  stroke="#e14944"
                  strokeWidth={2}
                  dot={{ fill: '#e14944', strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5, fill: '#e14944' }}
                />
                <Line
                  yAxisId="calls"
                  type="monotone"
                  dataKey="calls"
                  name="Calls"
                  stroke="#4285f4"
                  strokeWidth={2}
                  dot={{ fill: '#4285f4', strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5, fill: '#4285f4' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

        </TabsContent>
      </Tabs>
    </div>
  );
}
